import { useCurrentOrganization } from '~/auth/use-current-organization'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { gql } from '~/__generated__'
import { Section } from '~/ui/section'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { useValidationErrors } from '~/common/use-validation-errors'
import { TextField } from '~/fields/text-field'
import { TablePageLayout } from '~/layouts/table-page-layout'
import { Button } from '~/ui/button'
import { Form } from '~/ui/form'
import { useToast } from '~/hooks/use-toast'
import { Switch } from '~/ui/switch'
import { Label } from '~/ui/label'
import { Slider } from '~/ui/slider'

const mutation = gql(/* GraphQL */ `
  mutation UpdateOrganization($input: OrganizationUpdateInput!) {
    organizationUpdate(input: $input) {
      organization {
        id
        name
        crossfadeSeconds
        billingEmail
        explicitTracksAllowed
      }
    }
  }
`)

export const OrganizationSettingsScreen = () => {
  const { toast } = useToast()
  const { organization } = useCurrentOrganization()

  const formSchema = z.object({
    name: z.string().min(1, "Organization name is required"),
    crossfadeSeconds: z.number().min(0).max(12),
    billingEmail: z.string().email().optional().or(z.literal("")),
    explicitTracksAllowed: z.boolean(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      name: organization?.name ?? '',
      crossfadeSeconds: organization?.crossfadeSeconds ?? 4,
      billingEmail: organization?.billingEmail ?? '',
      explicitTracksAllowed: organization?.explicitTracksAllowed ?? false,
    },
  })

  const [exec, mutationResult] = useSafeMutation(mutation)
  useValidationErrors(form.setError, mutationResult)

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!organization) return

    const result = await exec({
      variables: {
        input: {
          id: organization.id,
          organizationInput: {
            name: values.name,
            crossfadeSeconds: values.crossfadeSeconds,
            billingEmail: values.billingEmail || null,
            explicitTracksAllowed: values.explicitTracksAllowed,
          },
        },
      },
    })

    if (!result.errors) {
      toast({
        title: 'Settings Updated',
        description: 'Organization settings have been successfully updated.',
      })
    }
  }

  if (!organization) {
    return (
      <TablePageLayout>
        <div>Loading...</div>
      </TablePageLayout>
    )
  }

  return (
    <TablePageLayout>
      <div className="flex flex-1 flex-col">
        <div className="bottom-1 mb-4 flex items-center justify-between border-b border-gray-300 py-4">
          <h1 className="text-2xl">Organization Settings</h1>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-8">
                <Section variant="grayBackground" className="space-y-4 p-0">
                  <TextField
                    control={form.control}
                    name="name"
                    label="Organization Name"
                    required
                  />
                  <TextField
                    control={form.control}
                    name="billingEmail"
                    label="Billing Email"
                    type="email"
                  />

                  <div className="space-y-2">
                    <Label htmlFor="crossfade-seconds">
                      Crossfade Duration: {form.watch('crossfadeSeconds')} seconds
                    </Label>
                    <Slider
                      id="crossfade-seconds"
                      min={0}
                      max={12}
                      step={1}
                      value={[form.watch('crossfadeSeconds')]}
                      onValueChange={(value) => form.setValue('crossfadeSeconds', value[0])}
                      className="w-full"
                    />
                    <p className="text-sm text-gray-600">
                      Controls how many seconds tracks overlap when transitioning between songs.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="explicit-tracks"
                      checked={form.watch('explicitTracksAllowed')}
                      onCheckedChange={(checked) => form.setValue('explicitTracksAllowed', checked)}
                    />
                    <Label htmlFor="explicit-tracks" className="text-sm font-medium">
                      Allow Explicit Tracks
                    </Label>
                  </div>
                  <p className="text-sm text-gray-600">
                    When enabled, playlists may include tracks marked as explicit content.
                  </p>
                </Section>

                <Section variant="grayBackground" className="space-y-4 p-6">
                  <h3 className="text-lg font-medium">Debug Information</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Stripe Customer ID: </span>
                      <span className="font-mono text-xs">
                        {organization?.stripeCustomerId || 'Not set'}
                      </span>
                    </div>
                    {(!organization?.stripeCustomerId) && (
                      <p className="text-amber-600 text-xs">
                        ⚠️ No Stripe customer ID set. Invoices won't be fetched from Stripe.
                      </p>
                    )}
                  </div>
                </Section>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4 border-t pt-10">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                Save Settings
              </Button>
              <button
                type="button"
                onClick={() => form.reset()}
                className="text-sm text-neutral-400 hover:text-neutral-600"
              >
                Reset Changes
              </button>
            </div>
          </form>
        </Form>
      </div>
    </TablePageLayout>
  )
}
